import { useCallback, useEffect, useState } from 'react'
import {
  acknowledgeAlert,
  fetchAlerts,
  fetchAverageSpeedtest,
  fetchAverageUtilizationPercent,
  fetchCurrentTraffic,
  fetchDailyStats,
  fetchDeviceUtilizationSeries,
  fetchDevicesDetails,
  fetchOfflineCount,
  fetchOnlineCount,
  fetchSchoolMarkers,
  fetchStatsByRegions,
  fetchTopConsumers,
  fetchTotalDevices,
  fetchTotalTraffic,
  fetchTrends,
  fetchUnstableRouters,
  fetchUtilizationSeries,
  runSpeedtest,
} from '../api/dashboard'
import { LOGS_TIME_RANGES, getTimeRangeParams } from '../utils/timeRanges'

const defaultSeries = { in: [], out: [] };

export const useDashboardData = (timeRange) => {
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [utilizationLoading, setUtilizationLoading] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [topUnstableRouters, setTopUnstableRouters] = useState([]);
  const [topConsumers, setTopConsumers] = useState([]);
  const [schoolMarkers, setSchoolMarkers] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [offlineCount, setOfflineCount] = useState(0);
  const [totalDevices, setTotalDevices] = useState(0);
  const [averageLoadPercent, setAverageLoadPercent] = useState(0);
  const [avgSpeedtest, setAvgSpeedtest] = useState({ download: 0, upload: 0 });
  const [utilizationData, setUtilizationData] = useState(defaultSeries);
  const [alerts, setAlerts] = useState([]);
  const [trends, setTrends] = useState({});
  const [totalTraffic, setTotalTraffic] = useState({ total_traffic_gb: 0, date: null });
  const [currentTraffic, setCurrentTraffic] = useState({ current_traffic_gb: 0 });
  const [dailyStats, setDailyStats] = useState([]);
  const [regionsStats, setRegionsStats] = useState([]);
  const [acknowledgeLoading, setAcknowledgeLoading] = useState(false);
  const [alertsPage, setAlertsPage] = useState(1);
  const [alertsPageSize, setAlertsPageSize] = useState(5);
  const [alertsTotalPages, setAlertsTotalPages] = useState(1);
  const [alertsTotalCount, setAlertsTotalCount] = useState(0);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const [
        online,
        offline,
        total,
        averageLoad,
        unstable,
        consumers,
        markers,
        deviceDetails,
        averageSpeedtest,
        onlineTrend,
        utilizationTrend,
        trafficTrend,
        trafficData,
        currentTrafficData,
        dailyStatsData,
        regionsStatsData,
      ] = await Promise.all([
        fetchOnlineCount(),
        fetchOfflineCount(),
        fetchTotalDevices(),
        fetchAverageUtilizationPercent(),
        fetchUnstableRouters(),
        fetchTopConsumers(),
        fetchSchoolMarkers(),
        fetchDevicesDetails(),
        fetchAverageSpeedtest(),
        fetchTrends('online_devices', 'daily', 1),
        fetchTrends('utilization', 'daily', 1),
        fetchTrends('traffic', 'daily', 1),
        fetchTotalTraffic(),
        fetchCurrentTraffic(),
        fetchDailyStats(),
        fetchStatsByRegions(),
      ]);

      setOnlineCount(online);
      setOfflineCount(offline);
      setTotalDevices(total);
      setAverageLoadPercent(Math.round(averageLoad));
      setTopUnstableRouters(Array.isArray(unstable) ? unstable : []);
      setTopConsumers(Array.isArray(consumers) ? consumers : []);
      setSchoolMarkers(Array.isArray(markers) ? markers : []);
      setDevices(Array.isArray(deviceDetails) ? deviceDetails : []);
      setAvgSpeedtest(averageSpeedtest);
      
      // Новые данные
      setTrends({
        online_devices: onlineTrend[0] || null,
        utilization: utilizationTrend[0] || null,
        traffic: trafficTrend[0] || null,
      });
      setTotalTraffic(trafficData);
      setCurrentTraffic(currentTrafficData);
      setDailyStats(Array.isArray(dailyStatsData) ? dailyStatsData : []);
      setRegionsStats(Array.isArray(regionsStatsData) ? regionsStatsData : []);
    } catch (error) {
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const alertsResponse = await fetchAlerts();
      const list = Array.isArray(alertsResponse) ? alertsResponse : [];
      const total = list.length;
      const startIndex = Math.max(0, (alertsPage - 1) * alertsPageSize);
      const endIndex = startIndex + alertsPageSize;
      setAlerts(list.slice(startIndex, endIndex));
      setAlertsTotalCount(total);
      setAlertsTotalPages(Math.max(1, Math.ceil(total / alertsPageSize)));
    } catch (error) {
      setAlerts([]);
      setAlertsTotalCount(0);
      setAlertsTotalPages(1);
    } finally {
      setAlertsLoading(false);
    }
  }, [alertsPage, alertsPageSize]);

  useEffect(() => {
    setAlertsPage(1);
  }, [alertsPageSize]);

  const loadUtilization = useCallback(async () => {
    setUtilizationLoading(true);
    try {
      const params = getTimeRangeParams(timeRange);
      const series = await fetchUtilizationSeries(params);
      setUtilizationData(series);
    } catch (error) {
      setUtilizationData(defaultSeries);
    } finally {
      setUtilizationLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadSummary();
    const interval = setInterval(loadSummary, 120000);
    const onFireUpdate = () => { loadSummary(); };
    window.addEventListener('fireIncidentUpdated', onFireUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener('fireIncidentUpdated', onFireUpdate);
    };
  }, [loadSummary]);

  useEffect(() => {
    loadUtilization();
  }, [loadUtilization]);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 120000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  const fetchDeviceSeries = useCallback(async (deviceIp, rangeKey) => {
    const params = getTimeRangeParams(rangeKey, LOGS_TIME_RANGES);
    return fetchDeviceUtilizationSeries(deviceIp, params);
  }, []);

  const startSpeedtest = useCallback(async (deviceIp) => runSpeedtest(deviceIp), []);

  const acknowledgeAlertByFingerprint = useCallback(
    async (fingerprint, options = {}) => {
      if (!fingerprint) {
        throw new Error('Не указан fingerprint алерта');
      }

      setAcknowledgeLoading(true);
      try {
        await acknowledgeAlert(fingerprint, options);
        await loadAlerts();
      } finally {
        setAcknowledgeLoading(false);
      }
    },
    [loadAlerts],
  );

  return {
    devices,
    topUnstableRouters,
    topConsumers,
    schoolMarkers,
    onlineCount,
    offlineCount,
    totalDevices,
    averageLoadPercent,
    avgSpeedtest,
    utilizationData,
    alerts,
    trends,
    totalTraffic,
    currentTraffic,
    dailyStats,
    regionsStats,
    summaryLoading,
    utilizationLoading,
    alertsLoading,
    alertsPage,
    alertsPageSize,
    alertsTotalPages,
    alertsTotalCount,
    acknowledgeLoading,
    reloadSummary: loadSummary,
    reloadUtilization: loadUtilization,
    reloadAlerts: loadAlerts,
    fetchDeviceSeries,
    startSpeedtest,
    setAlertsPage,
    setAlertsPageSize,
    acknowledgeAlert: acknowledgeAlertByFingerprint,
  };
};
